"use client";

import { getImageProps } from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

import { HeroBanner } from "../types";

interface HeroCarouselProps {
  banners: HeroBanner[];
}

export interface HeroSlide {
  desktopImageUrl: string;
  mobileImageUrl: string;
  preoptimized: boolean;
  link: string;
  title: string | null;
  subtitle: string | null;
  buttonText: string | null;
}

export function resolveHeroSlide(banner: HeroBanner): HeroSlide {
  const hasCustomDesktop = Boolean(banner.desktop_image);
  const isLegacyProducts =
    banner.desktop_image?.includes("hero_products_v2_desktop") ||
    (!hasCustomDesktop && banner.link.startsWith("/products"));
  const isLegacyConsultations =
    banner.desktop_image?.includes("consultations-hero") ||
    (!hasCustomDesktop && banner.link.startsWith("/consultations"));

  const desktopImageUrl = isLegacyProducts
    ? "/hero-products-desktop.avif"
    : isLegacyConsultations
      ? "/hero-consultation-desktop.avif"
      : banner.desktop_image || banner.mobile_image || "/hero-products-desktop.avif";

  const mobileImageUrl = isLegacyProducts
    ? "/hero-products-mobile.avif"
    : isLegacyConsultations
      ? "/hero-consultation-mobile.avif"
      : banner.mobile_image || banner.desktop_image || "/hero-products-mobile.avif";

  const preoptimized = isLegacyProducts || isLegacyConsultations;

  return {
    desktopImageUrl,
    mobileImageUrl,
    preoptimized,
    link: banner.link,
    title: banner.title,
    subtitle: banner.subtitle,
    buttonText: banner.button_text,
  };
}

const AUTOPLAY_DELAY_MS = 10_000;
const SWIPE_THRESHOLD_PX = 48;

const HeroCarousel = ({ banners }: HeroCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const slides = banners.map(resolveHeroSlide);

  useEffect(() => {
    if (slides.length < 2 || isPaused || !hasInteracted) return;

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, AUTOPLAY_DELAY_MS);

    return () => window.clearInterval(timer);
  }, [hasInteracted, isPaused, slides.length]);

  useEffect(() => {
    const markInteraction = () => setHasInteracted(true);
    window.addEventListener("pointerdown", markInteraction, { passive: true, once: true });
    window.addEventListener("keydown", markInteraction, { once: true });
    return () => {
      window.removeEventListener("pointerdown", markInteraction);
      window.removeEventListener("keydown", markInteraction);
    };
  }, []);

  if (slides.length === 0) return null;

  const showPrevious = () => {
    setActiveIndex((current) => (current - 1 + slides.length) % slides.length);
  };

  const showNext = () => {
    setActiveIndex((current) => (current + 1) % slides.length);
  };

  return (
    <section
      aria-roledescription="carousel"
      aria-label="Featured collections"
      className="hero-carousel relative h-[56vh] min-h-[400px] w-full overflow-hidden bg-gray-900 md:h-[68vh] lg:h-[72vh] lg:min-h-[600px] lg:max-h-[720px]"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
      onTouchStart={(event) => {
        touchStartX.current = event.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(event) => {
        if (touchStartX.current === null || slides.length < 2) return;
        const distance = event.changedTouches[0]?.clientX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(distance) < SWIPE_THRESHOLD_PX) return;
        if (distance > 0) showPrevious();
        else showNext();
      }}
    >
      {slides.map((slide, index) => {
        const isActive = index === activeIndex;
        const alt = slide.title
          ? `${slide.title} - AstroJi Special Collection`
          : "AstroJi Spiritual Store Special Collection & Rituals";
        const commonImageOptions = {
          alt,
          fill: true,
          quality: 75,
          sizes: "100vw",
          unoptimized: slide.preoptimized,
        } as const;
        const { props: mobileImageProps } = getImageProps({
          ...commonImageOptions,
          src: slide.mobileImageUrl,
        });
        const { props: desktopImageProps } = getImageProps({
          ...commonImageOptions,
          src: slide.desktopImageUrl,
        });

        return (
          <div
            key={`${slide.mobileImageUrl}-${index}`}
            aria-hidden={!isActive}
            className={`absolute inset-0 overflow-hidden transition-opacity duration-1000 ${
              isActive ? "z-10 opacity-100" : "pointer-events-none z-0 opacity-0"
            }`}
          >
            {(isActive || hasInteracted) && (
              <picture>
                <source
                  media="(min-width: 768px)"
                  srcSet={slide.preoptimized ? slide.desktopImageUrl : desktopImageProps.srcSet}
                  sizes={desktopImageProps.sizes}
                />
                <img
                  {...mobileImageProps}
                  alt={alt}
                  fetchPriority={index === 0 ? "high" : "low"}
                  loading={index === 0 ? "eager" : "lazy"}
                  className="object-cover object-center transition-transform duration-700 hover:scale-[1.015]"
                />
              </picture>
            )}

            <div className="absolute inset-0 bg-gradient-to-r from-black/68 via-black/36 to-black/5" />

            <div className="absolute inset-0 flex max-w-4xl flex-col items-start justify-center px-4 pb-12 sm:px-8 md:px-14 lg:px-20 xl:px-24">
              <h1 className="mb-3 max-w-[12ch] text-balance text-3xl font-bold leading-[1.08] text-white drop-shadow-sm sm:text-4xl md:mb-4 md:text-5xl lg:text-6xl">
                {slide.title}
              </h1>
              <p className="mb-5 max-w-xl text-sm leading-6 text-gray-100 sm:text-base md:mb-7 md:text-lg md:leading-8">
                {slide.subtitle}
              </p>
              <Link
                prefetch={false}
                href={slide.link}
                tabIndex={isActive ? undefined : -1}
                className="ui-button-accent group/btn rounded-lg px-5 sm:px-6"
              >
                <span className="relative flex items-center gap-2 text-sm">
                  {slide.buttonText}
                  <svg
                    aria-hidden="true"
                    className="h-4 w-4 transform transition-transform duration-200 group-hover/btn:translate-x-1 sm:h-5 sm:w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </Link>
            </div>
          </div>
        );
      })}

      {slides.length > 1 && (
        <div className="absolute inset-x-0 bottom-3 z-20 flex items-center justify-center gap-2" role="group" aria-label="Choose featured collection">
          {slides.map((slide, index) => (
            <button
              key={`${slide.mobileImageUrl}-control-${index}`}
              type="button"
              aria-label={`Show slide ${index + 1}`}
              aria-current={activeIndex === index ? "true" : undefined}
              onClick={() => setActiveIndex(index)}
              className={`h-2 rounded-full bg-white transition-[width,opacity] duration-300 ${
                activeIndex === index ? "w-6 opacity-100" : "w-2 opacity-50 hover:opacity-80"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default HeroCarousel;
